/**
 * @fileoverview Show Pacific-day capacity separately from provider billing. The backend owns reservation totals and the approved Intake cohort; this card exposes progress and an explicit pause/resume action without starting a new historical backfill. Refreshing reads state only.
 */
import React, { useEffect, useState } from 'react'
import api from '../../api'

const dollars = value => '$' + (Number(value || 0) / 1000000).toFixed(2)

export default function AiDailyBudget({ refreshKey }) {
    const [budget, setBudget] = useState(null)
    const [error, setError] = useState('')
    const [busy, setBusy] = useState(false)
    const [reload, setReload] = useState(0)
    useEffect(() => {
        let cancelled = false
        api.get('/admin/ai/budget').then(({ data }) => {
            if (!cancelled) { setBudget(data); setError('') }
        }).catch(() => { if (!cancelled) setError('Daily capacity is unavailable.') })
        return () => { cancelled = true }
    }, [refreshKey, reload])
    const change = async () => {
        setBusy(true)
        try {
            await api.post('/admin/ai/intake-backfill', {
                id: budget.intake.id, action: budget.intake.status === 'running' ? 'pause' : 'resume',
            })
            setReload(value => value + 1)
        } catch { setError('Could not change automatic Intake. Refresh and try again.') }
        finally { setBusy(false) }
    }
    if (!budget?.limit_microusd) return error ? <p role="status">{error}</p> : null
    const intake = budget.intake || {}
    const counts = intake.counts || {}
    const ready = (counts.draft_ready || 0) + (counts.already_contacted || 0)
    const waiting = counts.waiting_for_budget || 0
    return <section className="ai-spend__section" aria-labelledby="ai-daily-capacity">
        <header><h3 id="ai-daily-capacity">Daily capacity</h3><span>Resets midnight Pacific</span></header>
        <dl className="ai-spend__metrics">
            <div className="ai-spend__metric"><dt>Available for staff AI</dt><dd>{dollars(budget.available_microusd)}</dd>
                <small>{dollars(budget.limit_microusd)} total daily ceiling</small></div>
            <div className="ai-spend__metric"><dt>Automatic Intake</dt><dd>{dollars(budget.intake_committed_microusd)} / {dollars(budget.intake_limit_microusd)}</dd>
                <small>Daily allocation leaves {dollars(budget.staff_protected_microusd)} for staff</small></div>
            <div className="ai-spend__metric"><dt>Conservative cost estimate</dt><dd>{dollars(budget.spent_microusd)}</dd>
                <small>Plus {dollars(budget.reserved_microusd)} held for calls in progress or unknown outcomes</small></div>
        </dl>
        {intake.id && <div>
            <header><h4>Intake drafting · {intake.enabled ? intake.status : 'paused'}</h4>
                <button type="button" onClick={change} disabled={busy || !intake.enabled}>
                    {busy ? 'Saving…' : intake.status === 'running' ? 'Pause Intake' : 'Resume Intake'}
                </button></header>
            <progress aria-label="Intake outreach progress" value={ready} max={intake.total || 1} />
            <p>{ready} drafts ready or already contacted · {waiting} waiting for budget · {counts.held || 0} held for review · {intake.total} leads</p>
            {waiting > 0 && <p>Budget waits resume automatically. Staff AI can use the remaining app capacity.</p>}
        </div>}
        {error && <p role="alert">{error}</p>}
    </section>
}
